import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { constants } from 'constants/constants';
import { PAGES } from '../../registration-form-details.modal.component';
import { email } from '@certemy/common';
import moment from 'moment-mini';
import _get from 'lodash-es/get';
import _cloneDeep from 'lodash-es/cloneDeep';
import _mapKeys from 'lodash-es/mapKeys';
import { ComputedFrom } from '@certemy/common';
import { RegistrationFormField } from 'services/certs/certification.interface';
import { CertificationService } from 'services/certs/certification.service';
import { map, skip, switchMap, takeUntil, tap } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { convertToMap } from 'utils/helpers';
import { FIELDS_NAMES, COUNTRY_DEPENDENT_FIELDS } from 'constants/registration-form.constants';

const IGNORED_FIELDS = [FIELDS_NAMES.firstName, FIELDS_NAMES.lastName, FIELDS_NAMES.email];

@Component({
  selector: 'cm-complete-content',
  styleUrls: ['./complete-content.component.scss'],
  templateUrl: './complete-content.component.html',
})
export class CompleteContentComponent implements OnInit, OnDestroy {
  @Output() formSubmit: EventEmitter<any> = new EventEmitter();
  @Output() screenChange: EventEmitter<string> = new EventEmitter();
  @Input() certificate;
  @Input() isStepSubmitted = false;
  @Input() formData;
  @Input() step;
  @Input()
  set registrationFormFields(fields: RegistrationFormField[]) {
    this._registrationFormFields = _cloneDeep(fields);
  }
  get registrationFormFields() {
    return this._registrationFormFields;
  }
  private _registrationFormFields: RegistrationFormField[];

  FIELD_TYPES = constants.FORM_FIELD_TYPE_IDS;
  showErrors = false;
  registrationForm: UntypedFormGroup;
  readonly CONSTANTS = constants;
  showRequiredError = false;
  showEmailError = false;
  private updateStates$ = new Subject();
  private readonly onDestroy$ = new Subject<void>();

  @ComputedFrom('step')
  get savedFields() {
    return this.step.content.fields.filter(field => !field.hidden && !IGNORED_FIELDS.includes(field.name));
  }

  @ComputedFrom('step')
  get hiddenFieldsMap() {
    return this.step.content.fields
      .filter(field => field.hidden)
      .reduce((fieldsMap, field) => {
        fieldsMap = {
          ...fieldsMap,
          [field.id]: field.value,
        };
        return fieldsMap;
      }, {});
  }

  @ComputedFrom('savedFields')
  get savedFieldsMap() {
    return this.convertFieldsToMap(this.savedFields);
  }

  @ComputedFrom('registrationFormFields')
  get registrationFormFieldsMap() {
    return this.convertFieldsToMap(this.registrationFormFields);
  }

  @ComputedFrom('registrationFormFields')
  private get validFromDateId() {
    const element = this.registrationFormFields.find(el => el.name === 'Valid From Date') || { field_id: null };
    return element.field_id;
  }

  @ComputedFrom('registrationFormFields')
  private get validUntilDateId() {
    const element = this.registrationFormFields.find(el => el.name === 'Valid Until Date') || { field_id: null };
    return element.field_id;
  }

  @ComputedFrom('registrationFormFields')
  get countryField() {
    return this.getFormFieldByName(FIELDS_NAMES.country);
  }

  @ComputedFrom('registrationFormFields')
  get stateField() {
    return this.getFormFieldByName(FIELDS_NAMES.state);
  }

  @ComputedFrom('registrationFormFields')
  get countriesMap() {
    return convertToMap(this.countryField.options, 'id');
  }

  constructor(
    private certificationService: CertificationService,
    private fb: UntypedFormBuilder,
    private ngbActiveModal: NgbActiveModal,
  ) {}

  ngOnInit() {
    this.checkForCountryValue(this.savedFields).subscribe(() => {
      this.registrationForm = this.fb.group(this.createForm());

      if (this.savedFieldsMap[this.countryField.field_id]) {
        this.subscribeOnCountryChange();
      }

      if (this.isStepSubmitted) {
        this.registrationForm.disable();
      }
    });

    this.updateStates$
      .pipe(
        switchMap(countryId => this.getStatesByCountryId(countryId)),
        takeUntil(this.onDestroy$),
      )
      .subscribe(states => this.setStatesOptions(states));
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  getElementId(element: RegistrationFormField): string {
    return `form_${element.field_id}`;
  }

  private createForm() {
    return this.savedFields.reduce((formControls, element: RegistrationFormField) => {
      const value = _get(this.savedFieldsMap[element.field_id], 'value', '');
      const disabled = _get(this.savedFieldsMap[element.field_id], 'read_only', false);
      const validators = [];

      if (element.required) {
        validators.push(Validators.required);
      }

      if (element.type_id === this.FIELD_TYPES.EMAIL) {
        validators.push(email);
      }

      formControls[element.field_id] = [{ value, disabled }, Validators.compose(validators)];

      return formControls;
    }, {});
  }

  closeModal() {
    this.ngbActiveModal.close();
  }

  backToMainPage() {
    this.screenChange.emit(PAGES.MAIN_START);
  }

  submitForm() {
    // Disabled form is considered to be invalid (https://github.com/angular/angular/issues/11432)

    if (this.registrationForm.valid || (!this.registrationForm.valid && this.registrationForm.disabled)) {
      this.showErrors = false;
      this.showRequiredError = false;
      this.showEmailError = false;
      const formValue = this.replaceFieldIdWithFieldInstanceId(this.registrationForm.getRawValue());
      this.formSubmit.emit({ ...formValue, ...this.hiddenFieldsMap });
    } else {
      this.showErrors = true;
      this.checkRequiredFields();
      this.checkEmails();
    }
  }

  checkRequiredFields() {
    this.showRequiredError = this.savedFields.some(field => {
      const { errors } = this.registrationForm.controls[field.field_id];
      return field.required && errors && errors.required;
    });
  }

  checkEmails() {
    this.showEmailError = this.savedFields.some(field => {
      const isEmail = field.type_id === this.FIELD_TYPES.EMAIL;
      const control = this.registrationForm.controls[field.field_id];

      if (isEmail && control.invalid) {
        return control.errors.hasOwnProperty('email');
      }
    });
  }

  getDatepickerMinDate(elem): string | null {
    if (elem.field_id === this.validFromDateId) {
      return moment().format('YYYY-MM-DD');
    } else if (elem.field_id === this.validUntilDateId) {
      return this.getDynamicFormElementValue(this.validFromDateId) || moment().format('YYYY-MM-DD');
    } else {
      return null;
    }
  }

  getDatepickerMaxDate(elem): string | null {
    if (elem.field_id === this.validFromDateId) {
      return this.getDynamicFormElementValue(this.validUntilDateId);
    } else {
      return null;
    }
  }

  setStatesOptions(states) {
    const formControl = this.registrationForm.controls[this.stateField.field_id];

    if (states && states.length) {
      this.stateField.options = states;
      this.stateField.noStates = false;
      formControl.setValidators(Validators.required);
    } else {
      this.stateField.noStates = true;
      formControl.clearValidators();
    }
    formControl.updateValueAndValidity();
  }

  checkCountryFieldFilled(field) {
    const { is_default, name } = field;
    const value = this.getDynamicFormElementValue(this.countryField.field_id);

    if (is_default && COUNTRY_DEPENDENT_FIELDS.includes(name)) {
      return !!value;
    }
    return true;
  }

  checkForCountryValue(formFields) {
    const countryControl = formFields.find(({ field_id }) => field_id === this.countryField.field_id);
    let countryValue = null;

    if (countryControl) {
      countryValue = countryControl.value;
    }

    if (countryValue) {
      return this.getStatesByCountryId(countryValue).pipe(
        tap(states => {
          this.stateField.options = states;
          this.stateField.noStates = !!states.length;
        }),
      );
    } else {
      return of(null);
    }
  }

  private getDynamicFormElementValue(id) {
    const formGroup = this.registrationForm.controls[id];
    return _get(formGroup, 'value', null);
  }

  private convertFieldsToMap<T extends { field_id: number }>(fields: T[]): { [key: number]: T } {
    return fields.reduce((fieldsMap, field) => {
      fieldsMap[field.field_id] = field;
      return fieldsMap;
    }, {});
  }

  private replaceFieldIdWithFieldInstanceId(fieldsMap) {
    return _mapKeys(fieldsMap, (value, field_id) => (this.savedFieldsMap[field_id] as any).id);
  }

  private subscribeOnCountryChange() {
    const countryControl = this.registrationForm.controls[this.countryField.field_id];
    const stateControl = this.registrationForm.controls[this.stateField.field_id];

    countryControl.valueChanges.subscribe(this.updateStates$);

    countryControl.valueChanges.pipe(skip(1)).subscribe(() => stateControl.setValue(''));

    this.updateStates$.next(countryControl.value);
  }

  private getFormFieldByName(fieldName) {
    return this.registrationFormFields.find(el => el.is_default && el.name === fieldName);
  }

  private getStatesByCountryId(countryId) {
    if (countryId) {
      return this.certificationService.getRegistrationFormCountryStates(countryId, this.step.certification_id).pipe(
        map(states =>
          states.map(state => {
            state.id = state.id.toString();
            return state;
          }),
        ),
      );
    } else {
      return of([]);
    }
  }
}
